import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Container, Paper } from '@mui/material';
import { AdminPanelSettings, Lock } from '@mui/icons-material';

/**
 * Composant de protection de route pour les administrateurs
 * Vérifie si l'utilisateur est connecté ET est administrateur
 */
const AdminRoute = ({ children }) => {
  // Récupérer les infos utilisateur depuis localStorage
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  
  // État de chargement initial
  const [loading, setLoading] = React.useState(true);
  const [isAdmin, setIsAdmin] = React.useState(false);

  React.useEffect(() => {
    // Simuler une vérification (en production, faire un appel API)
    const checkAdminStatus = async () => {
      try {
        if (!user) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        // Vérifier si l'utilisateur a le flag is_admin
        // En production, faire un appel API pour vérifier le statut admin
        // const response = await fetch('/api/auth/verify-admin', {
        //   headers: { 'Authorization': `Bearer ${token}` }
        // });
        // const data = await response.json();
        // setIsAdmin(data.isAdmin);

        // Pour le moment, vérifier le flag is_admin dans le localStorage
        setIsAdmin(user.is_admin === true);
      } catch (error) {
        console.error('Erreur vérification admin:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  // Affichage pendant le chargement
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: '#1a1a2e'
      }}>
        <CircularProgress 
          size={60} 
          sx={{ 
            color: '#667eea',
            mb: 3
          }} 
        />
        <Typography sx={{ color: '#ccc', fontSize: '1.1rem' }}>
          Vérification des permissions...
        </Typography>
      </Box>
    );
  }

  // Si pas connecté, rediriger vers login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Si connecté mais pas admin, afficher message d'erreur
  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#1a1a2e',
        py: 4
      }}>
        <Paper sx={{
          bgcolor: '#2a2a4e',
          borderRadius: 3,
          p: 4,
          textAlign: 'center',
          border: '2px solid rgba(244, 67, 54, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <Box sx={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            bgcolor: 'rgba(244, 67, 54, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Lock sx={{ fontSize: 50, color: '#f44336' }} />
          </Box>
          
          <Typography variant="h4" sx={{ 
            color: '#fff', 
            fontWeight: 700,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}>
            <AdminPanelSettings sx={{ fontSize: 32 }} />
            Accès refusé
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#f44336', 
            fontWeight: 600,
            mb: 3
          }}>
            Zone réservée aux administrateurs
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: '#ccc',
            mb: 1,
            lineHeight: 1.6
          }}>
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </Typography>
          
          <Typography variant="body2" sx={{ 
            color: '#888',
            mb: 4
          }}>
            Cette section est réservée exclusivement aux administrateurs de l'application.
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'center',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Box
              component="button"
              onClick={() => window.history.back()}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                border: '2px solid rgba(255, 255, 255, 0.2)',
                bgcolor: 'transparent',
                color: '#fff',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Retour
            </Box>
            
            <Box
              component="button"
              onClick={() => window.location.href = '/'}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                border: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)'
                }
              }}
            >
              Accueil
            </Box>
          </Box>
          
          <Box sx={{ 
            mt: 4, 
            pt: 3, 
            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Connecté en tant que: <strong style={{ color: '#888' }}>{user.email}</strong>
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Si admin, afficher le contenu protégé
  return children;
};

export default AdminRoute;
